import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { formatToZMW, handlePrint, rootUrl } from '../../../helpers';
import { FaEye, FaPrint } from 'react-icons/fa';
import { useOutletContext } from 'react-router-dom';
import Modal from '../../../components/Modal';
import { useAuth } from '../../../context/auth-context';

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [filters, setFilters] = useState({ constituency: '', sub: '', head: '', sof: '', year: '' });
  const {allData, setAllData} = useOutletContext();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [selectedProjectFilter, setSelectedProjectFilter] = useState('');

  const auth = useAuth()
  const fetchData = async () => {
    const { data } = await axios.get(rootUrl(`/getAllProjects/${auth.user.id}`));
    setProjects(data.data);
    
    setFilteredProjects(data.data);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const componentRef = useRef(null);

  useEffect(() => {
    const filtered = projects.filter((item) =>
      (filters.constituency === '' || String(item.constituency) === filters.constituency) &&
      (filters.sub === '' || String(item.sub) === filters.sub) &&
      (filters.head === '' || String(item.head) === filters.head) &&
      (filters.sof === '' || item.sof === filters.sof) &&
      (filters.year === '' || String(item.year) === filters.year)
    );
    setFilteredProjects(filtered);
  }, [filters, projects]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    let parsedValue = value;
    if (name === 'sof') parsedValue = value === '' ? '' : value;
    setFilters((prev) => ({ ...prev, [name]: parsedValue }));
  };

  const getUniqueValues = (key) => {
    return [...new Set(projects.map((item) => String(item[key])).filter(Boolean))];
  };

  const groupBy = (array, key) => {
    return array.reduce((result, item) => {
      const groupKey = String(item[key]);
      (result[groupKey] = result[groupKey] || []).push(item);
      return result;
    }, {});
  };

  // When an action button is clicked, fetch the project's transactions and show the modal.
  const handleAction = async (project) => {
    try {
      // Replace with your actual endpoint. Expecting an object like { data: { data: [/* transactions */] } }
      const response = await axios.get(rootUrl(`/getProject/${project.id}`));
      setTransactions(response.data.data);
      setSelectedProject(project);
      setIsModalVisible(true);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  // Filter projects by selected project name before grouping
  const filteredProjectsByName = selectedProjectFilter
    ? filteredProjects.filter(p => p.name === selectedProjectFilter)
    : filteredProjects;
  const groupedByConstituency = groupBy(filteredProjectsByName, 'constituency');

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-semibold text-gray-800 mb-4">Project Report</h1>
      <div className="flex space-x-4 mb-4">
        <select
          name="project"
          value={selectedProjectFilter}
          onChange={e => setSelectedProjectFilter(e.target.value)}
          className="border border-gray-300 rounded px-3 py-2 text-sm"
        >
          <option value="">All Projects</option>
          {projects.map((proj) => (
            <option key={proj.id} value={proj.name}>{proj.name}</option>
          ))}
        </select>
        {/* Year Filter Dropdown */}
        <select
          name="year"
          value={filters.year}
          onChange={handleFilterChange}
          className="border border-gray-300 rounded px-3 py-2 text-sm"
        >
          <option value="">All Years</option>
          {getUniqueValues('year').map((value) => (
            <option key={value} value={value}>{value}</option>
          ))}
        </select>
        {['constituency', 'sof', 'head', 'sub'].map((filterKey) => (
          <select
            key={filterKey}
            name={filterKey}
            value={filters[filterKey]}
            onChange={handleFilterChange}
            className="border border-gray-300 rounded px-3 py-2 text-sm"
          >
            <option value="">All {filterKey}</option>
            {getUniqueValues(filterKey).map((value) => (
              <option key={value} value={value}>
                {filterKey === 'sof'
                  ? allData.sof.find((a) => a.id == value)?.name
                  : value}
              </option>
            ))}
          </select>
        ))}
      </div>
      <div className="overflow-x-auto shadow-lg rounded-lg">
        <table className="min-w-full bg-white border border-gray-300">
          <thead className="bg-blue-950 text-white">
            <tr>
              <th className="px-4 py-2 text-left text-sm font-medium">Constituency</th>
              <th className="px-4 py-2 text-left text-sm font-medium">SOF</th>
              <th className="px-4 py-2 text-left text-sm font-medium">Type</th>
              <th className="px-4 py-2 text-left text-sm font-medium">Sub</th>
              <th className="px-4 py-2 text-left text-sm font-medium">Name</th>
              <th className="px-4 py-2 text-left text-sm font-medium">Year</th>
              <th className="px-4 py-2 text-left text-sm font-medium">Amount</th>
              <th className="px-4 py-2 text-left text-sm font-medium">Paid</th>
              <th className="px-4 py-2 text-left text-sm font-medium">Received</th>
              <th className="px-4 py-2 text-left text-sm font-medium">Contract Balance</th>
              <th className="px-4 py-2 text-left text-sm font-medium">Balance</th>
              <th className="px-4 py-2 text-left text-sm font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(groupedByConstituency).map(([constituency, projectsByConstituency]) => {
              const groupedByHead = groupBy(projectsByConstituency, 'head');
              const constituencyTotal = projectsByConstituency.reduce(
                (sum, item) => sum + parseFloat(item.amount || 0),
                0
              );
              const constituencyPaid = projectsByConstituency.reduce(
                (sum, item) => sum + parseFloat(item.paid_cash || 0),
                0
              );

              const constituencyRec = projectsByConstituency.reduce(
                (sum, item) => sum + parseFloat(item.rec_cash || 0),
                0
              );
              return (
                <React.Fragment key={constituency}>
                  {Object.entries(groupedByHead).map(([head, projectsByHead]) => {
                    const groupedBySub = groupBy(projectsByHead, 'sub');
                    const headTotal = projectsByHead.reduce(
                      (sum, item) => sum + parseFloat(item.amount || 0),
                      0
                    );
                    const headPaid = projectsByHead.reduce(
                      (sum, item) => sum + parseFloat(item.paid_cash || 0),
                      0
                    );

                    const headRec = projectsByHead.reduce(
                      (sum, item) => sum + parseFloat(item.rec_cash || 0),
                      0
                    );
                    return (
                      <React.Fragment key={head}>
                        {Object.entries(groupedBySub).map(([sub, projectsBySub]) => {
                          const subTotal = projectsBySub.reduce(
                            (sum, item) => sum + parseFloat(item.amount || 0),
                            0
                          );
                          const subPaid = projectsBySub.reduce(
                            (sum, item) => sum + parseFloat(item.paid_cash || 0),
                            0
                          );
                          const subRec = projectsBySub.reduce(
                            (sum, item) => sum + parseFloat(item.rec_cash || 0),
                            0
                          );
                          return (
                            <React.Fragment key={sub}>
                              {projectsBySub.map((item) => (
                                <tr key={item.id} className="border-t hover:bg-gray-100">
                                  <td className="px-4 py-2 text-sm text-gray-800">{constituency}</td>
                                  <td className="px-4 py-2 text-sm text-gray-800">
                                    {allData.sof.find((a) => a.id === item.sof)?.name || ''}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-800">{head || ''}</td>
                                  <td className="px-4 py-2 text-sm text-gray-800">{sub || ''}</td>
                                  <td className="px-4 py-2 text-sm text-gray-800">{item.name || ''}</td>
                                  <td className="px-4 py-2 text-sm text-gray-800">{item.year || ''}</td>
                                  <td className="px-4 py-2 text-sm text-gray-800">{formatToZMW(item.amount)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-800">{formatToZMW(item.paid_cash)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-800">{formatToZMW(item.rec_cash)}</td>
                                  <td className="px-4 py-2 text-sm text-gray-800">{formatToZMW(item.amount - parseFloat(item.paid_cash))}</td>
                                  <td className="px-4 py-2 text-sm text-gray-800">{formatToZMW(item.rec_cash - parseFloat(item.paid_cash))}</td>
                                  <td className="px-4 py-2 text-sm">
                                    <button
                                      onClick={() => handleAction(item)}
                                      className="p-1 hover:bg-gray-200 rounded"
                                    >
                                      <FaEye className="text-lg text-blue-600 hover:text-blue-800" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                              <tr className="bg-gray-300 font-semibold">
                                <td className="px-4 py-2"></td>
                                <td className="px-4 py-2"></td>
                                <td className="px-4 py-2"></td>
                                <td className="px-4 py-2">Total</td>
                                <td className="px-4 py-2"></td>
                                <td className="px-4 py-2"></td>
                                <td className="px-4 py-2">{formatToZMW(subTotal)}</td>
                                <td className="px-4 py-2">{formatToZMW(subPaid)}</td>
                                <td className="px-4 py-2">{formatToZMW(subRec)}</td>
                                <td className="px-4 py-2">{formatToZMW(subTotal - subPaid)}</td>
                                <td className="px-4 py-2">{formatToZMW(subRec - subPaid)}</td>
                                <td className="px-4 py-2"></td>
                              </tr>
                            </React.Fragment>
                          );
                        })}
                        <tr className="bg-gray-500 font-bold">
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2">Total</td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2">{formatToZMW(headTotal)}</td>
                          <td className="px-4 py-2">{formatToZMW(headPaid)}</td>
                          <td className="px-4 py-2">{formatToZMW(headRec)}</td>
                          <td className="px-4 py-2">{formatToZMW(headTotal - headPaid)}</td>
                          <td className="px-4 py-2">{formatToZMW(headRec - headPaid)}</td>
                          <td className="px-4 py-2"></td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                  <tr className="bg-blue-950 text-white font-bold">
                    <td className="px-4 py-2">{constituency} Total</td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2">{formatToZMW(constituencyTotal)}</td>
                    <td className="px-4 py-2">{formatToZMW(constituencyPaid)}</td>
                    <td className="px-4 py-2">{formatToZMW(constituencyRec)}</td>
                    <td className="px-4 py-2">{formatToZMW(constituencyTotal - constituencyPaid)}</td>
                    <td className="px-4 py-2">{formatToZMW(constituencyRec - constituencyPaid)}</td>
                    <td className="px-4 py-2"></td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {isModalVisible && selectedProject && (
        <Modal
          title="Project Transactions"
          onClose={() => {
            setIsModalVisible(false);
            setSelectedProject(null);
            setTransactions([]);
          }}
        >
  <div> 
     <button
                                    onClick={() => handlePrint(componentRef)}
                                    className="bg-blue-950 text-white px-4 py-2 rounded m-2"
                                    >
                                    <FaPrint/>
                                     </button>         
          <div ref={componentRef} className="p-4">
            <div className='flex justify-center'>
            <div>
               <img className="w-[150px] h-[150px]" src={rootUrl(`/${allData?.council[0]?.path}`)} alt="Council Logo" />
               <span className="font-bold text-xl">{allData.council[0]?.name.toUpperCase()}</span>
            </div>
            </div>

            <hr className='my-2' />
           
            {/* Project Details Header */}
            <div className="mb-4 border-b pb-2">
              <h2 className="text-2xl font-bold">{selectedProject.name}</h2>
              <p className="text-gray-700">{selectedProject.description || 'No description available.'}</p>
              <div className="mt-2 flex flex-wrap gap-4">
                <div>
                  <strong>Constituency:</strong> {selectedProject.constituency}
                </div>
                <div>
                  <strong>Head:</strong> {selectedProject.head}
                </div>
                <div>
                  <strong>Sub:</strong> {selectedProject.sub}
                </div>
                <div>
                  <strong>SOF:</strong>{' '}
                  {allData.sof.find((a) => a.id === selectedProject.sof)?.name || ''}
                </div>
              </div>
            </div>
            <table className=" bg-white border border-gray-300">
              <tr className="border border-gray-300 text-left"><th className="px-4 py-2">Contract Sum</th><td className="px-4 py-2">{formatToZMW(selectedProject.amount)}</td></tr>
              <tr className="border border-gray-300 text-left"><th className="px-4 py-2">Paid Amount</th><td className="px-4 py-2">{formatToZMW(selectedProject.paid_cash)}</td></tr>
              <tr className="border border-gray-300 text-left"><th className="px-4 py-2">Received Amount</th><td className="px-4 py-2">{formatToZMW(selectedProject.rec_cash)}</td></tr>
              <tr className="border border-gray-300 text-left"><th className="px-4 py-2">Contract Balance</th><td className="px-4 py-2">{formatToZMW(selectedProject.amount - selectedProject.paid_cash)}</td></tr>
              <tr className="text-left"><th className="px-4 py-2">Balance</th><td className="px-4 py-2">{formatToZMW(selectedProject.rec_cash - selectedProject.paid_cash)}</td></tr>
            </table>
            {/* Transactions Table */}
            <div>
              <h3 className="text-xl font-semibold my-2">Transactions</h3>
              {transactions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-300">
                    <thead className="bg-blue-950 text-white">
                      <tr>
                        <th className="px-4 py-2">Date</th>
                        <th className="px-4 py-2">Description</th>
                        <th className="px-4 py-2">Ref</th>
                        <th className="px-4 py-2">PV</th>
                        <th className="px-4 py-2">Received</th>
                        <th className="px-4 py-2">Paid</th>
                        <th className="px-4 py-2">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        let runningBalance = 0;
                        return transactions.map((transaction) => {
                          const received = transaction.type === 'income' ? parseFloat(transaction.amount || 0) : 0;
                          const paid = transaction.type === 'expense' ? parseFloat(transaction.amount || 0) : 0;
                          runningBalance += received - paid;
                          return (
                            <tr key={transaction.id} className="border-t">
                              <td className="px-4 py-2">{transaction.date.substring(0,10)}</td>
                              <td className="px-4 py-2">{transaction.description}</td>
                              <td className="px-4 py-2">{transaction.cheque}</td>
                              <td className="px-4 py-2">{transaction.voucher}</td>
                              <td className="px-4 py-2">{formatToZMW(received)}</td>
                              <td className="px-4 py-2">{formatToZMW(paid)}</td>
                              <td className="px-4 py-2">{formatToZMW(runningBalance)}</td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                    <tfoot>
                <tr className="border-t font-bold">
                <td className="px-4 py-2">Total</td>
                <td className="px-4 py-2"></td>
                <td className="px-4 py-2"></td>
                <td className="px-4 py-2"></td>
                <td className="px-4 py-2">{formatToZMW(transactions.reduce((acc, curr) => acc + (curr.type === 'income' ? parseFloat(curr.amount || 0) : 0), 0))}</td>
                <td className="px-4 py-2">{formatToZMW(transactions.reduce((acc, curr) => acc + (curr.type === 'expense' ? parseFloat(curr.amount || 0) : 0), 0))}</td>
                <td className="px-4 py-2">{formatToZMW(transactions.reduce((acc, curr) => acc + ((curr.type === 'income' ? parseFloat(curr.amount || 0) : 0) - (curr.type === 'expense' ? parseFloat(curr.amount || 0) : 0)), 0))}</td>
                </tr>
            </tfoot>
                  </table>

                  <hr className="my-5" />

      <div className="grid grid-cols-2 gap-4">
        <div>
        <p><strong>Prepared By:</strong> __________________</p>
        <p><strong>Signature:</strong> __________________</p>
        <p><strong>Designation:</strong> __________________</p>
        <p><strong>Date:</strong> __________________</p>
        </div>

        <div>
        <p><strong>Verified By:</strong> __________________</p>
        <p><strong>Signature:</strong> __________________</p>
        <p><strong>Designation:</strong> __________________</p>
        <p><strong>Date:</strong> __________________</p>
        </div>
      </div>
                </div>
                
              ) : (
                <p>No transactions found for this project.</p>
              )}
            </div>
          </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
